import { Box, Tooltip, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { Dayjs } from 'dayjs';
import { MembershipYearResult } from 'src/modules/sending/functions/membershipYear';
import { toCustomerSendDate } from 'src/modules/sending/functions/customerSendDate';

// Förklaring av friperioden (tooltip) – hålls kort men fullständig så kunder slipper fråga vad det innebär.
const FRIPERIOD_INFO =
  'Friperioden är en period innan medlemsåret slutar. En medlem som betalar under friperioden får betalningen ' +
  'räknad för nästa medlemsår i stället för det innevarande – medlemskapet rullar alltså vidare till nästa år.';

interface SendingFriPeriodTimelineProps {
  sendDate: Dayjs | null;
  payDate: Dayjs | null;
  result: MembershipYearResult | null;
}

// Visuell tidslinje: utskicksdatum → betaldatum, med friperioden markerad fram till förbundets förfallodatum.
// Datumen läggs på var sin rad (ladder) med en tunn kopplingslinje ner till linjen, så etiketterna aldrig krockar.
export const SendingFriPeriodTimeline = ({ sendDate, payDate, result }: SendingFriPeriodTimelineProps) => {
  if (!sendDate || !result) {
    return (
      <Typography variant='body2' color='text.secondary'>
        Välj utskicksdatum för att se tidslinjen.
      </Typography>
    );
  }

  const { friStart, target, straddles, hasFriPeriod } = result;

  // Tidslinjens spann: från tidigaste till senaste relevanta datum, med lite marginal i kanterna.
  const candidates = [sendDate, friStart, target, payDate].filter(Boolean) as Dayjs[];
  const minMs = Math.min(...candidates.map((d) => d.valueOf()));
  const maxMs = Math.max(...candidates.map((d) => d.valueOf()));
  const span = Math.max(1, maxMs - minMs);
  const pad = span * 0.08;
  const lo = minMs - pad;
  const range = span + pad * 2;
  const clampPct = (d: Dayjs) => Math.max(0, Math.min(100, ((d.valueOf() - lo) / range) * 100));

  const friLeft = clampPct(friStart);
  const friRight = clampPct(target);
  const friColor = straddles ? 'error.main' : 'info.main';

  const fmt = (d: Dayjs) => d.format('D MMM');

  // Kundens utskicksdatum visas en dag tidigare än DB-datumet (produktionsdagen är dagen innan). Prickens POSITION
  // ligger kvar på det riktiga utskicksdatumet (beräkningarna) – bara etikettens datumtext skiftas −1 dag.
  const customerSend = toCustomerSendDate(sendDate);

  // Markörer i den ordning de läggs på varsin etikettrad (raderna behöver inte följa datumordningen på linjen).
  // dateLabel överstyr datumtexten för en markör (annars visas fmt(date)); används för Utskick-markörens kund-datum.
  const markers: { key: string; date: Dayjs; label: string; color: string; dateLabel?: string }[] = [
    {
      key: 'send',
      date: sendDate,
      label: 'Utskick',
      color: 'primary.main',
      dateLabel: customerSend ? customerSend.format('ddd D MMM') : fmt(sendDate),
    },
    ...(payDate ? [{ key: 'pay', date: payDate, label: 'Sista betaldatum', color: straddles ? 'error.main' : 'success.main' }] : []),
    // Förfallodatumet (friperiodens slut) behöver ingen etikett – bara datumet räcker, det framgår av sammanhanget.
    { key: 'target', date: target, label: '', color: 'text.secondary' },
  ];

  // Radhöjd för etikett-laddern + kantmedveten justering så texten inte flödar ut i kanterna.
  const ROW = 22;
  const labelsHeight = markers.length * ROW;
  const anchorSx = (p: number) => {
    if (p < 12) return { transform: 'translateX(0)', textAlign: 'left' as const };
    if (p > 88) return { transform: 'translateX(-100%)', textAlign: 'right' as const };
    return { transform: 'translateX(-50%)', textAlign: 'center' as const };
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Friperiod-etikett ovanför bandet (visas bara när förbundet har en friperiod). */}
      <Box sx={{ position: 'relative', height: 18, mb: 0.75 }}>
        {hasFriPeriod && (
          <Typography
            variant='caption'
            sx={{
              position: 'absolute',
              left: `${friLeft}%`,
              ...anchorSx(friLeft),
              whiteSpace: 'nowrap',
              fontWeight: 700,
              color: friColor,
            }}
          >
            Friperiod börjar {fmt(friStart)}
            <Tooltip title={FRIPERIOD_INFO} arrow enterTouchDelay={0}>
              <InfoOutlined sx={{ fontSize: 14, ml: 0.4, verticalAlign: 'text-bottom', cursor: 'help', opacity: 0.85 }} />
            </Tooltip>
          </Typography>
        )}
      </Box>

      {/* Själva linjen: rundad grå bana med markerat friperiod-band (bara om friperiod finns) och tydliga datumprickar. */}
      <Box sx={{ position: 'relative', height: 10, borderRadius: 5, bgcolor: 'grey.200' }}>
        {hasFriPeriod && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${friLeft}%`,
              width: `${Math.max(0, friRight - friLeft)}%`,
              bgcolor: (t) => (straddles ? t.palette.error.light : t.palette.info.light),
              borderRadius: 5,
            }}
          />
        )}
        {markers.map((m) => (
          <Box
            key={m.key}
            sx={{
              position: 'absolute',
              top: '50%',
              left: `${clampPct(m.date)}%`,
              transform: 'translate(-50%, -50%)',
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: m.color,
              border: '3px solid #fff',
              boxShadow: 1,
              zIndex: 2,
            }}
          />
        ))}
      </Box>

      {/* Etikett-ladder: varje datum på egen rad med en tunn kopplingslinje upp till sin prick. */}
      <Box sx={{ position: 'relative', height: labelsHeight, mt: 0.5 }}>
        {markers.map((m, i) => {
          const p = clampPct(m.date);
          return (
            <Box key={m.key}>
              {/* Kopplingslinje från linjen ner till etikettens rad (döljs för första raden som ligger direkt under). */}
              {i > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: `${p}%`,
                    width: '1px',
                    height: i * ROW,
                    bgcolor: 'grey.300',
                  }}
                />
              )}
              <Typography
                variant='caption'
                sx={{
                  position: 'absolute',
                  top: i * ROW,
                  left: `${p}%`,
                  ...anchorSx(p),
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                  color: m.color,
                  lineHeight: 1.2,
                }}
              >
                {m.label && `${m.label} `}
                <Box component='span' sx={{ color: 'text.secondary', fontWeight: m.label ? 400 : 600 }}>
                  {m.dateLabel ?? fmt(m.date)}
                </Box>
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
